import React from 'react';
import { FaHome, FaChartLine, FaLightbulb, FaExclamationTriangle } from 'react-icons/fa';
import { CalculatorResults } from '../types';
import { formatCurrency, formatPercentage } from '../utils/calculations';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { Separator } from './ui/separator';

interface ResultsDisplayProps {
  results: CalculatorResults | null;
  loading: boolean;
}

const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ results, loading }) => {
  if (loading) {
    return (
      <Card>
        <CardContent className="space-y-4 pt-6">
          <Skeleton className="h-6 w-full" />
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!results) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <FaHome className="mx-auto text-4xl text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground mb-2">
            Enter your information to calculate your housing budget
          </h3>
          <p className="text-muted-foreground">
            Fill out the form on the left to see your personalized housing recommendations.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { housing, taxes, recommendations } = results;
  const affordabilityRatio = housing.recommendedHousingBudget / housing.netMonthlyIncome;

  return (
    <div className="space-y-6">
      {/* Main Budget Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <FaHome className="text-primary text-xl" />
            <span>Your Housing Budget</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Primary Budget Highlight */}
          <Alert className="bg-primary/10 border-primary/20">
            <AlertDescription>
              <div className="text-sm text-primary font-medium">Recommended Monthly Housing Budget</div>
              <div className="text-3xl font-bold text-primary">
                {formatCurrency(housing.recommendedHousingBudget)}
              </div>
              <div className="text-sm text-primary">
                30% of gross income • {formatPercentage(affordabilityRatio)} of take-home pay
              </div>
            </AlertDescription>
          </Alert>

          {/* Income Breakdown */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-3">
                <div className="text-sm text-muted-foreground">Gross Monthly Income</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(housing.grossMonthlyIncome)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <div className="text-sm text-muted-foreground">Take-Home Income</div>
                <div className="text-lg font-semibold">
                  {formatCurrency(housing.netMonthlyIncome)}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Breakdown */}
          <div className="space-y-2">
            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Housing Budget:</span>
              <span className="font-medium text-primary">
                {formatCurrency(housing.recommendedHousingBudget)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Remaining for Other Expenses:</span>
              <span className="font-medium">
                {formatCurrency(housing.remainingForOtherExpenses)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Effective Tax Rate:</span>
              <span>
                {formatPercentage(taxes.effectiveRate)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Housing Options Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <FaChartLine className="text-secondary text-xl" />
            <span>Housing Options</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Rent Option */}
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium mb-2">Rental Budget</h4>
              <div className="text-2xl font-bold mb-1">
                {formatCurrency(housing.maxRentBudget)}
              </div>
              <div className="text-sm text-muted-foreground">
                Maximum monthly rent including utilities
              </div>
            </CardContent>
          </Card>

          {/* Purchase Option */}
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium mb-3">Home Purchase</h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Maximum Home Price:</span>
                  <span className="font-semibold">
                    {formatCurrency(housing.maxHomePurchasePrice)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Recommended Down Payment (20%):</span>
                  <span className="font-semibold">
                    {formatCurrency(housing.recommendedDownPayment)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Est. Monthly Payment (PITI):</span>
                  <span className="font-semibold">
                    {formatCurrency(housing.estimatedMortgagePayment)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Recommendations Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <FaLightbulb className="text-yellow-500 text-xl" />
            <span>Recommendations for Trade Workers</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {recommendations.map((recommendation, index) => (
              <li key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-sm">{recommendation}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Warning Card (if applicable) */}
      {affordabilityRatio > 0.40 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <FaExclamationTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription>
            <div>
              <h3 className="text-lg font-medium text-yellow-800 mb-2">Budget Warning</h3>
              <p className="text-sm text-yellow-700">
                Your housing costs may be high relative to your take-home pay ({formatPercentage(affordabilityRatio)}). 
                Consider looking for more affordable options or ways to increase your income to maintain financial stability.
              </p>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default ResultsDisplay;